import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import {
  People,
  School,
  TrendingUp,
  Groups,
} from '@mui/icons-material';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface Statistics {
  total_alumni: number;
  by_graduation_year: { [key: string]: number };
  by_major: { [key: string]: number };
  by_faculty: { [key: string]: number };
  by_gender: { [key: string]: number };
}

const StatisticsSection = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v1/alumni/public/statistics`);
      setStatistics(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('ไม่สามารถโหลดสถิติได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  const getGenderLabel = (gender: string) => {
    const labels: { [key: string]: string } = {
      male: 'ชาย',
      female: 'หญิง',
      other: 'อื่นๆ',
    };
    return labels[gender] || gender;
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth="xl">
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  if (!statistics) return null;

  // Prepare data for charts
  const yearData = Object.entries(statistics.by_graduation_year)
    .map(([year, count]) => ({
      year: parseInt(year),
      count,
      yearLabel: (parseInt(year) + 543).toString(), // Convert to Buddhist Era
    }))
    .sort((a, b) => a.year - b.year)
    .slice(-10); // Last 10 years

  const majorData = Object.entries(statistics.by_major)
    .map(([major, count]) => ({ major, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8); // Top 8 majors

  // Generate colors from theme
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    '#4CAF50',
    '#FF9800',
    '#9C27B0',
    '#00BCD4',
    '#E91E63',
    '#795548',
  ];

  return (
    <Box
      id="statistics-section"
      sx={{
        py: 8,
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="xl">
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            textAlign: 'center',
          }}
        >
          สถิติศิษย์เก่า
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
            mb: 6,
            textAlign: 'center',
          }}
        >
          ข้อมูลสถิติและจำนวนศิษย์เก่าในแต่ละสาขา
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <People sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {statistics.total_alumni.toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  จำนวนศิษย์เก่าทั้งหมด
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                color: 'white',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <School sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {Object.keys(statistics.by_major).length}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  สาขาวิชา
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)`,
                color: 'white',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {yearData.length > 0 ? yearData[yearData.length - 1].count : 0}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  บัณฑิตใหม่ล่าสุด
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, #FF9800 0%, #F57C00 100%)`,
                color: 'white',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Groups sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {Object.keys(statistics.by_graduation_year).length}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  รุ่นศิษย์เก่า
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={4}>
          {/* Alumni by Graduation Year */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '100%', p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                จำนวนศิษย์เก่าตามปีที่สำเร็จการศึกษา
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={yearData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="yearLabel"
                    style={{ fontSize: '12px' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                    }}
                    formatter={(value: any) => [`${value} คน`, 'จำนวน']}
                    labelFormatter={(label) => `ปี พ.ศ. ${label}`}
                  />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {yearData.map((_entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={chartColors[index % chartColors.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          {/* Alumni by Major */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '100%', p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                จำนวนศิษย์เก่าตามสาขาวิชา
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={majorData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" style={{ fontSize: '12px' }} />
                  <YAxis
                    dataKey="major"
                    type="category"
                    width={150}
                    style={{ fontSize: '11px' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                    }}
                    formatter={(value: any) => [`${value} คน`, 'จำนวน']}
                  />
                  <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                    {majorData.map((_entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={chartColors[index % chartColors.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          {/* Gender Distribution */}
          {Object.keys(statistics.by_gender).length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  สัดส่วนศิษย์เก่าตามเพศ
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(statistics.by_gender).map(([gender, count]) => (
                    <Grid item xs={12} sm={6} md={3} key={gender}>
                      <Box
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          borderRadius: 2,
                          backgroundColor: 'background.default',
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                          {count}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {getGenderLabel(gender)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({((count / statistics.total_alumni) * 100).toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default StatisticsSection;
